import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import Turnstile from 'react-turnstile';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, success, error
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    if (!captchaToken) {
      setStatus('error');
      setMessage('Please complete the CAPTCHA check.');
      return;
    }

    setLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('You are already subscribed!');
        }
        throw error;
      }

      setStatus('success');
      setMessage('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      setStatus('error');
      setMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='w-full py-16 text-gray-900 dark:text-white px-4 bg-gray-50 dark:bg-[#0A1220] border-b border-gray-200 dark:border-gray-800 relative overflow-hidden transition-colors duration-300'>
      {/* Background/Structure */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-emerald-50/50 dark:from-blue-900/10 dark:to-emerald-900/10 opacity-50"></div>

      <div className='max-w-[1240px] mx-auto grid lg:grid-cols-3 gap-8 relative z-10 items-center'>
        <div className='lg:col-span-2 my-4'>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 border-blue-200 text-blue-600 dark:bg-blue-500/10 dark:border border-blue-500/20 dark:text-blue-400 text-xs font-mono mb-4">
            <span className="relative flex h-2 w-2">
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            WEEKLY INSIGHTS
          </div>
          <h1 className='md:text-4xl sm:text-3xl text-2xl font-bold py-2 leading-tight'>
            Master Data Analytics, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600 dark:from-blue-400 dark:to-emerald-400">One Week at a Time.</span>
          </h1>
          <p className='text-gray-600 dark:text-gray-400 mt-2 max-w-xl'>Sign up to our newsletter and stay up to date. We send weekly SQL challenges and industry insights.</p>
        </div>

        <div className='my-4 flex flex-col'>
          <form onSubmit={handleSubscribe} className='flex flex-col sm:flex-row items-center justify-between w-full gap-4'>
            <div className="w-full relative">
              <input
                className={`p-3 flex w-full rounded-lg bg-white dark:bg-[#162032] border ${status === 'error' ? 'border-red-500/50' : 'border-gray-200 dark:border-gray-700'} text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-mono text-sm`}
                type='email'
                placeholder='Enter Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || status === 'success'}
              />
            </div>
            <button
              type="submit"
              disabled={loading || status === 'success'}
              className={`rounded-lg font-medium w-full sm:w-[200px] px-6 py-3 transition-all duration-300 shadow-lg shadow-blue-900/20 whitespace-nowrap flex items-center justify-center gap-2 ${status === 'success'
                ? 'bg-emerald-600 text-white cursor-default'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : status === 'success' ? (
                <>Subscribed <FaCheckCircle /></>
              ) : (
                'Notify Me'
              )}
            </button>
          </form>

          {/* CAPTCHA Area - Invisible unless challenge needed */}
          <div className="mt-0 w-0 h-0 overflow-hidden">
            <Turnstile
              sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              onError={() => setMessage("CAPTCHA failed. Please refresh.")}
              onExpire={() => setCaptchaToken(null)}
              theme="dark"
              appearance="interaction-only"
            />
          </div>

          {/* Status Message Area */}
          <div className="mt-2 h-6">
            {status === 'success' && (
              <p className="text-emerald-400 text-sm flex items-center gap-2 animate-fadeIn">
                <FaCheckCircle /> {message}
              </p>
            )}
            {status === 'error' && (
              <p className="text-red-400 text-sm flex items-center gap-2 animate-fadeIn">
                <FaExclamationCircle /> {message}
              </p>
            )}
            {status === 'idle' && (
              <p className='text-xs text-gray-500 font-mono'>
                We care about the protection of your data. Read our{' '}
                <Link to="/privacy-policy" className='text-blue-400 font-semibold cursor-pointer hover:underline'>Privacy Policy.</Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Newsletter;
